import React from 'react'
import PropTypes from 'prop-types'

// import Error from '@shared/components/error'
import { payload } from '@shared/libs/category'
import { handleApiError, isAMPEnable } from '@shared-submodule/utils'
import dynamic from 'next/dynamic'

const FantasyArticleDetail = dynamic(() => import('@shared/components/fantasyArticleDetail'))
const CategoryContent = dynamic(() => import('@shared/components/categoryContent'))
const SeriesFantasyArticle = dynamic(() => import('@shared/components/series/seriesFantasyArticle'))
const FantasyArticleContent = dynamic(() => import('@shared/components/articleDetail/fantasyArticleContent'))
const ArticleContent = dynamic(() => import('@shared/components/articleDetail/articleContent'))
const ArticleDetail = dynamic(() => import('@shared/components/articleDetail'))

function FantasyArticle({
  seoData,
  fantasyArticle,
  error,
  type,
  category,
  playerData,
  article,
  latestArticles,
  currentSeriesData,
  rankingTabData,
  playingXI,
  match,
  seriesData
}) {
  if (type === 'ct') {
    return (
      <CategoryContent
        seoData={seoData}
        category={category}
        isFantasyArticle
        currentSeriesData={currentSeriesData}
        rankingTabData={rankingTabData}>
        {() => <SeriesFantasyArticle data={fantasyArticle} category={category} />}
      </CategoryContent>
    )
  } else if (type === 'ar') {
    return (
      <ArticleDetail seoData={seoData} article={article} type="article" currentSeriesData={currentSeriesData}>
        <ArticleContent article={article} />
      </ArticleDetail>
    )
  } else {
    return (
      <FantasyArticleDetail
        seoData={seoData}
        article={fantasyArticle?.data?.getFrontFantasyArticle}
        type="fantasyArticle"
        currentSeriesData={currentSeriesData}>
        <FantasyArticleContent
          data={fantasyArticle?.data?.getFrontFantasyArticle}
          playingXI={playingXI}
          match={match}
          seriesData={seriesData}
        />
      </FantasyArticleDetail>
    )
  }
}

FantasyArticle.propTypes = {
  seoData: PropTypes.object,
  fantasyArticle: PropTypes.object,
  error: PropTypes.any,
  type: PropTypes.string,
  category: PropTypes.object,
  article: PropTypes.object,
  playerData: PropTypes.array,
  latestArticles: PropTypes.object,
  currentSeriesData: PropTypes.object,
  rankingTabData: PropTypes.object,
  playingXI: PropTypes.object,
  match: PropTypes.object,
  seriesData: PropTypes.array
}

// export default Error(FantasyArticle)
export default FantasyArticle

export async function getServerSideProps({ req, res, params, query, resolvedUrl }) {
  try {
    const { token } = req?.cookies
    const slug = params.slug
    const parallelCalls = (await import('@shared-submodule/utils')).parallelCalls
    const [queryGraphql, GET_SEO_DATA, checkRedirectionStatus, CURRENT_SERIES_LIST, allRoutes] = await parallelCalls([
      import('@shared/components/queryGraphql').then(module => module.default),
      import('@graphql/article/article.query').then(module => module.GET_SEO_DATA),
      import('@shared-submodule/utils').then(module => module.checkRedirectionStatus),
      import('@graphql/globalwidget/rankings.query').then(module => module.CURRENT_SERIES_LIST),
      import('@shared-submodule/sm-constants/allRoutes').then(module => module?.allRoutes)
    ])
    const newSlug = allRoutes.fantasyCricketTips.substring(1) + slug.join('/')

    const [idData, currentSeriesData] = await parallelCalls([
      queryGraphql(GET_SEO_DATA, { input: { sSlug: newSlug } }),
      queryGraphql(CURRENT_SERIES_LIST)
    ])
    res.setHeader('Cache-Control', 'public, max-age=420')
    // Check Redirection
    const { redirectStatus, eCode, returnObj, props } = checkRedirectionStatus(idData?.getSeoData)
    if (redirectStatus && props) {
      res.statusCode = eCode
      return { props }
    } else if (redirectStatus) return returnObj

    if (idData?.getSeoData?.eType === 'ct') {
      // Category
      const [GET_CATEGORY_BY_ID, GET_FANTASY_ARTICLE_OF_CATEGORY, RANKINGS] = await parallelCalls([
        import('@graphql/category/category.query').then(module => module.GET_CATEGORY_BY_ID),
        import('@graphql/series/home.query').then(module => module.GET_FANTASY_ARTICLE_OF_CATEGORY),
        import('@graphql/globalwidget/rankings.query').then(module => module.RANKINGS)
      ])
      const [categoryData, fantasyArticle, rankingTabData] = await parallelCalls([
        queryGraphql(GET_CATEGORY_BY_ID, { input: { _id: idData?.getSeoData?.iId } }, token),
        queryGraphql(GET_FANTASY_ARTICLE_OF_CATEGORY, { input: { ...payload(7), iId: idData?.getSeoData?.iId } }),
        queryGraphql(RANKINGS, { input: { eMatchType: 'Tests', eRankType: 'Teams', nLimit: 5, nSkip: 0 } })
      ])
      return {
        props: {
          seoData: idData?.getSeoData,
          fantasyArticle: fantasyArticle?.listFrontTagCategoryFantasyArticle,
          type: idData?.getSeoData?.eType,
          category: categoryData.getCategoryByIdFront,
          currentSeriesData: currentSeriesData,
          rankingTabData: rankingTabData
        }
      }
    }
    if (idData?.getSeoData?.eType === 'fa') {
      // Fantasy article
      const parallelCalls = (await import('@shared-submodule/utils')).parallelCalls
      const [GET_FANTASY_DETAILS, GET_MATCH_PLAYING_XI, MATCH_DETAIL, FETCH_SERIES_STATS_TYPE] = await parallelCalls([
        import('@graphql/fantasy-tips/fantasy-tips.query').then(module => module.GET_FANTASY_DETAILS),
        import('@graphql/match/match.query').then(module => module.GET_MATCH_PLAYING_XI),
        import('@graphql/match/match.query').then(module => module.MATCH_DETAIL),
        import('@graphql/series/stats.query').then(module => module.FETCH_SERIES_STATS_TYPE)
      ])
      const value = await queryGraphql(GET_FANTASY_DETAILS, { input: { _id: idData?.getSeoData?.iId } })
      const { data: playingXI } = await queryGraphql(GET_MATCH_PLAYING_XI, {
        input: { iMatchId: value?.data?.getFrontFantasyArticle?.iMatchId }
      })
      const { data: match } = await queryGraphql(MATCH_DETAIL, { input: { _id: value?.data?.getFrontFantasyArticle?.iMatchId } })
      const { data: seriesData } = await queryGraphql(FETCH_SERIES_STATS_TYPE, { input: { eGroupTitle: 'All' } })
      // const playerData = await queryGraphql(GET_FANTASY_PLAYER_DATA, { input: { iMatchId: value?.data?.getFrontFantasyArticle?.oMatch?._id, ePlatformType: value?.data?.getFrontFantasyArticle?.ePlatformType } })
      // Check amp is enable from backend or not
      const { isRedirect, redirectObj } = isAMPEnable(value?.data?.getFrontFantasyArticle, query)
      if (isRedirect) return redirectObj
      return {
        props: {
          seoData: idData?.getSeoData,
          fantasyArticle: value,
          type: idData?.getSeoData?.eType,
          currentSeriesData: currentSeriesData,
          playingXI: playingXI?.getMatchOverviewFront,
          match: match?.getMatchById,
          seriesData: seriesData?.fetchSeriesStatsTypes
          // playerData: playerData?.data?.listFantasyPlayersInfo
        }
      }
    }
    if (idData?.getSeoData?.eType === 'ar') {
      // Article
      const GET_ARTICLE_DETAILS = (await import('@graphql/article/article.query')).GET_ARTICLE_DETAILS
      const { data } = await queryGraphql(GET_ARTICLE_DETAILS, { input: { _id: idData?.getSeoData?.iId } })

      // Check amp is enable from backend or not
      const { isRedirect, redirectObj } = isAMPEnable(data?.getArticleFront, query)
      if (isRedirect) return redirectObj
      return {
        props: {
          seoData: idData?.getSeoData,
          article: data?.getArticleFront,
          type: idData?.getSeoData?.eType,
          currentSeriesData: currentSeriesData
        }
      }
    }
    return { notFound: true }
  } catch (e) {
    res.setHeader('Cache-Control', 'no-cache')
    const status = handleApiError(e, resolvedUrl)
    return status
    // return { props: { error: JSON.stringify(e) } }
  }
}
